import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { RangeInput } from '@sorare/core/src/__generated__/globalTypes';
import { RangeValues } from '@sorare/core/src/atoms/inputs/RangeSlider';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { NonNullableRangeInput } from '@sorare/core/src/components/compose/types';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { filters } from '@sorare/core/src/lib/glossary';
import { powerAlgorithm } from '@sorare/core/src/lib/slider';

import { RangeSlider } from './RangeSlider';

const Wrapper = styled.div`
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-150);
  padding: var(--double-unit);

  &.disabled {
    opacity: 0.25;
    cursor: not-allowed;
    & > * {
      pointer-events: none;
      cursor: default;
    }
  }
`;

const priceAlgorithm = powerAlgorithm({ power: 20 });

type Props = {
  min: number;
  max: number;
  priceRange: Nullable<RangeInput>;
  setPriceRange: (starterOddsBasisPointsRange: NonNullableRangeInput) => void;
};

export const PriceFilter = ({ min, max, priceRange, setPriceRange }: Props) => {
  const { fiatCurrency } = useCurrentUserContext();
  const rangeValues: RangeValues = useMemo(() => {
    return {
      low: priceRange?.min || min,
      high: priceRange?.max || max,
    };
  }, [max, min, priceRange?.max, priceRange?.min]);

  return (
    <Vertical>
      <SBHorizontal>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...filters.price} />
        </LabelM>
      </SBHorizontal>
      <Wrapper>
        <RangeSlider
          initialValues={rangeValues}
          onChange={values => {
            setPriceRange({ min: values.low, max: values.high });
          }}
          min={min}
          max={max}
          icon={
            <LabelL bold color="var(--c-white)">
              {fiatCurrency.symbol}
            </LabelL>
          }
          algorithm={priceAlgorithm}
          fromInput={value => Number(Math.round(value).toFixed(2))}
          toInput={value => value.toFixed(2)}
        />
      </Wrapper>
    </Vertical>
  );
};
